import React from 'react';

type Props = {
  children: React.ReactNode;
};

type State = {
  error: Error | null;
};

/**
 * 렌더 중 발생한 예외를 이 지점에서 막아 주는 컴포넌트입니다.
 *
 * React 는 렌더 도중 예외가 나면 트리 전체를 언마운트하기 때문에,
 * 화면 한 조각의 오류가 페이지 전체를 빈 화면으로 만든다.
 * 2026-09-01 공사기간 팝업 사고가 정확히 그 형태였다
 * (달력이 던진 예외 하나로 백오피스 화면 전체가 하얘짐).
 *
 * 여기서 예외를 잡아 두면 같은 종류의 결함이 다시 나더라도
 * 사용자는 빈 화면 대신 안내와 새로고침 수단을 보게 된다.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {error: null};
  }

  static getDerivedStateFromError(error: Error): State {
    return {error};
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // 운영에서 원인 추적이 가능하도록 남긴다.
    // eslint-disable-next-line no-console
    console.error('[BackOffice] 화면 렌더 중 오류', error, info.componentStack);
  }

  render() {
    const {error} = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          padding: '40px',
          fontSize: '16px',
          lineHeight: 1.6,
          color: '#191919',
        }}>
        <p style={{fontWeight: 700, marginBottom: '12px'}}>
          화면을 표시하는 중 오류가 발생했습니다.
        </p>
        <p style={{marginBottom: '20px'}}>
          새로고침 후에도 같은 문제가 계속되면 개발팀에 아래 내용을 전달해
          주세요.
        </p>
        <pre
          style={{
            padding: '12px',
            background: '#f5f5f5',
            borderRadius: '4px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}>
          {error.message}
        </pre>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            cursor: 'pointer',
          }}>
          새로고침
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
